import { useMemo, useState } from 'react';
import { format } from 'date-fns';

import {
  CompletionChart,
  CostStats,
  DurationStats,
  EndpointSuccessChart,
  ErrorAnalysis,
} from '../../components/stats';
import type { DateRange } from '../../types';
import {
  type DateRangePreset,
  getDateRangeFromPreset,
  useAdsStats,
} from '../hooks/useAdsStats';

/**
 * Ads Statistics page — same six-panel layout as lightning + interior
 * + video stats pages so users get a consistent surface across all
 * four sub-apps. Endpoint Avg Duration is dropped (the ads pipeline
 * doesn't time per-endpoint shares, every job is one engine call per
 * format) leaving five panels visible.
 */
export function AdsStatsPage() {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('last7days');
  const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });

  const dateRange = useMemo(() => {
    if (selectedPreset === 'custom') return customRange;
    return getDateRangeFromPreset(selectedPreset);
  }, [selectedPreset, customRange]);

  const { stats, loading, error } = useAdsStats(dateRange);

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-soft flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-surface-600">Range:</span>
            <div className="flex rounded border border-surface-200 overflow-hidden">
              {(['today', 'last7days', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => setSelectedPreset(preset)}
                  className={`px-3 py-1 text-xs font-medium transition-all duration-200 ${
                    selectedPreset === preset
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {preset === 'today' && 'Today'}
                  {preset === 'last7days' && '7 Days'}
                  {preset === 'last30days' && '30 Days'}
                  {preset === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {selectedPreset === 'custom' && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={customRange.from ? format(customRange.from, 'yyyy-MM-dd') : ''}
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    from: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-xs">to</span>
              <input
                type="date"
                value={customRange.to ? format(customRange.to, 'yyyy-MM-dd') : ''}
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    to: e.target.value ? new Date(e.target.value + 'T23:59:59') : null,
                  }))
                }
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}

          {stats && (
            <div className="ml-auto flex items-center gap-3 text-xs text-surface-500">
              <span>
                <strong className="text-surface-700">{stats.totalJobs}</strong> jobs
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded bg-danger-light border border-danger/30 text-danger-dark text-xs">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-24 text-slate-500">
          Loading statistics…
        </div>
      )}

      {stats && !loading && (
        <div className="grid grid-cols-1 gap-3 auto-rows-[minmax(24rem,auto)] sm:auto-rows-[minmax(22rem,auto)] lg:grid-cols-3">
          <DurationStats stats={stats.duration} title="Duration Analysis" />
          <CostStats stats={stats.cost} title="Cost Analysis" />
          <EndpointSuccessChart stats={stats.endpointStats} title="Engine Success Rate" />
          <CompletionChart stats={stats.completion} title="Completion Rate" />
          <ErrorAnalysis errors={stats.errors} title="Error Analysis" />
        </div>
      )}

      {!loading && !error && stats && stats.totalJobs === 0 && (
        <div className="flex items-center justify-center py-24 text-slate-500">
          <div className="text-center">
            <p className="font-medium mb-1">No ads jobs in this period</p>
            <p className="text-sm">Try selecting a different date range</p>
          </div>
        </div>
      )}
    </div>
  );
}
